import { createFileRoute, Link } from "@tanstack/react-router";
import { FaqSection, faqSchema, type FaqItem } from "@/components/PageFaq";
import { breadcrumbSchema } from "@/lib/schema";

const URL = "https://palstars.com/cards/jetragon-ssp-market-value";
const TITLE = "Jetragon TCG Card Value & Stats Tracker | Pal Stars";
const DESCRIPTION =
  "Jetragon Palworld TCG card tracker: confirmed Legends Awaken set information, what is still unpublished, and where its price will appear once the card list goes live.";

const FAQ: FaqItem[] = [
  {
    q: "Is there an official Jetragon Palworld TCG card?",
    a: "Jetragon is confirmed as featured artwork for the Legends Awaken expansion, but the official card list has not published a printed card number, rarity, effect text or scan for it yet. Until it does, no Jetragon card exists to price.",
  },
  {
    q: "What is a Jetragon Legend card?",
    a: "There is no printed \"Legend\" rarity in the Palworld card game. The English rarity set runs C, U, R, RR, SR, SP, SSP, OSR, SSS, TD, TSR, TSP and PR — \"Legend\" comes from the video game's legendary Pals, not from the TCG.",
  },
  {
    q: "How much will the Jetragon card be worth?",
    a: "We will not publish a value before the card is printed. Once the official list assigns it a code and rarity, it enters the card database and its asking board and TCGplayer sold history appear automatically on its card page.",
  },
  {
    q: "When does Legends Awaken release?",
    a: "Legends Awaken is dated 30 October 2026 with roughly 100 card types plus parallels across five colors. Individual card codes and rarities are published closer to release.",
  },
];

export const Route = createFileRoute("/cards/jetragon-ssp-market-value")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "article" },
      { property: "og:url", content: URL },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      breadcrumbSchema([
        ["Home", "/"],
        ["Palworld TCG Cards", "/cards"],
        ["Jetragon card value", "/cards/jetragon-ssp-market-value"],
      ]),
      faqSchema(FAQ, URL),
    ],
  }),
  component: JetragonValuePage,
});

function JetragonValuePage() {
  return (
    <div className="mx-auto max-w-4xl px-4 sm:px-6 py-10 space-y-8">
      <header>
        <p className="text-xs uppercase tracking-widest text-primary">Palworld TCG · Legends Awaken tracker</p>
        <h1 className="mt-2 text-3xl sm:text-4xl font-bold">
          Official Jetragon TCG Card Rarity &amp; Market Price Analytics
        </h1>
        <p className="aeo-answer mt-3 max-w-3xl text-sm text-muted-foreground">
          Jetragon is confirmed as featured Legends Awaken artwork, but no printed Jetragon card has been published yet:
          the official card list carries no card number, rarity, effect text or scan for it. This page tracks the card's
          status and will carry its live price board the day the official entry appears.
        </p>
      </header>

      <section className="glass rounded-2xl p-6 space-y-3 text-sm text-muted-foreground">
        <h2 className="text-xl font-bold text-foreground">Confirmed today</h2>
        <ul className="space-y-2">
          <li>
            <strong className="text-foreground">Set:</strong> Legends Awaken (BP02), releasing 30 October 2026.
          </li>
          <li>
            <strong className="text-foreground">Scope:</strong> roughly 100 card types plus parallel treatments across
            five colors.
          </li>
          <li>
            <strong className="text-foreground">Jetragon:</strong> featured in official set promotion. Card number,
            rarity and effect text unpublished.
          </li>
          <li>
            <strong className="text-foreground">Price:</strong> none. We do not estimate values for unprinted cards, and
            no sealed-product presale figure is a card price.
          </li>
        </ul>
      </section>

      <section className="glass rounded-2xl p-6 space-y-3 text-sm text-muted-foreground">
        <h2 className="text-xl font-bold text-foreground">Where the price will show up</h2>
        <p>
          As soon as the official list assigns Jetragon a printed code, the card enters our database and every board it
          belongs on fills automatically — asking quotes, TCGplayer completed sales, graded tiers and index weighting.
          Until then, read the market it is joining through{" "}
          <Link to="/tcg-price-index" className="text-primary hover:underline">
            Palworld TCG price index market trends
          </Link>
          .
        </p>
        <ul className="space-y-2">
          <li>
            <Link to="/prices" className="text-primary hover:underline">
              Palworld TCG Price Chart
            </Link>
          </li>
          <li>
            <Link to="/tcg-sold-prices" className="text-primary hover:underline">
              Palworld TCG last sold prices index
            </Link>
          </li>
          <li>
            <Link to="/cards/frostallion-ssp-market-value" className="text-primary hover:underline">
              Frostallion card status tracker
            </Link>
          </li>
        </ul>
      </section>

      <FaqSection items={FAQ} heading="Jetragon TCG card FAQ" />
    </div>
  );
}
